import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  useUpdateHospitalMutation,
  useCreateHospitalMutation,
} from "../services/hospitalService";
import toast from "react-hot-toast";
import {
  Button,
  TextField,
  CircularProgress,
  Box,
  Modal,
  Paper,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
  Tooltip,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { X } from "lucide-react";
import {
  AccessTime,
  Email,
  LocalHospital,
  LocationOn,
  Map,
  Phone,
} from "@mui/icons-material";
import MapPicker from "./MapPicker";

interface Address {
  street: string;
  city: string;
  state: string;
  postCode: string;
  country: string;
}

interface Location {
  type: string;
  coordinates: number[];
}

interface HospitalFormData {
  name: string;
  images: string[];
  address: Address;
  location: Location;
  phone: string;
  email: string;
  description: string;
  specialities: string[];
  totalBeds: number;
  availableBeds: number;
  icuBeds: number;
  icuAvailableBeds: number;
  ventilatorBeds: number;
  ventilatorAvailableBeds: number;
  operatingHours: any;
}

const HospitalFormModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  hospital?: any;
}> = ({ open, onClose, onSuccess, hospital }) => {
  const [loading, setLoading] = useState(false);
  const isEdit = !!hospital;

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<HospitalFormData>({
    defaultValues: {
      name: "",
      images: [],
      address: { street: "", city: "", state: "", postCode: "", country: "" },
      location: { type: "Point", coordinates: [] },
      phone: "",
      email: "",
      description: "",
      specialities: [],
      totalBeds: 0,
      availableBeds: 0,
      icuBeds: 0,
      icuAvailableBeds: 0,
      ventilatorBeds: 0,
      ventilatorAvailableBeds: 0,
      operatingHours: {
        monday: { start: "", end: "" },
        tuesday: { start: "", end: "" },
        wednesday: { start: "", end: "" },
        thursday: { start: "", end: "" },
        friday: { start: "", end: "" },
        saturday: { start: "", end: "" },
        sunday: { start: "", end: "" },
      },
    },
  });

  const formValues = watch();

  // Reset form when user data changes
  useEffect(() => {
    if (hospital) {
      reset({
        name: hospital.name || "",
        images: [],
        address: {
          street: hospital.address.street || "",
          city: hospital.address.city || "",
          state: hospital.address.state || "",
          postCode: hospital.address.postCode || "",
          country: hospital.address.country || "",
        },
        location: {
          type: "Point",
          coordinates: hospital.location.coordinates || [],
        },
        phone: hospital.phone || "",
        email: hospital.email || "",
        description: hospital.description || "",
        specialities: hospital.specialities || [],
        totalBeds: hospital.totalBeds || 0,
        availableBeds: hospital.availableBeds || 0,
        icuBeds: hospital.icuBeds || 0,
        icuAvailableBeds: hospital.icuAvailableBeds || 0,
        ventilatorBeds: hospital.ventilatorBeds || 0,
        ventilatorAvailableBeds: hospital.ventilatorAvailableBeds || 0,
        operatingHours: hospital.operatingHours || null,
      });
    } else {
      reset({
        name: "",
        images: [],
        address: {
          street: "",
          city: "",
          state: "",
          postCode: "",
          country: "",
        },
        location: {
          type: "Point",
          coordinates: [],
        },
        phone: "",
        email: "",
        description: "",
        specialities: [],
        totalBeds: 0,
        availableBeds: 0,
        icuBeds: 0,
        icuAvailableBeds: 0,
        ventilatorBeds: 0,
        ventilatorAvailableBeds: 0,
        operatingHours: {
          monday: { start: "", end: "" },
          tuesday: { start: "", end: "" },
          wednesday: { start: "", end: "" },
          thursday: { start: "", end: "" },
          friday: { start: "", end: "" },
          saturday: { start: "", end: "" },
          sunday: { start: "", end: "" },
        },
      });
    }
  }, [hospital, reset]);

  const [createHospital] = useCreateHospitalMutation();
  const [updateHospital] = useUpdateHospitalMutation();

  const onSubmit = async (data: HospitalFormData) => {
    setLoading(true);
    try {
      if (isEdit && hospital?._id) {
        await updateHospital({ _id: hospital._id, ...data }).unwrap();
        toast.success("Hospital updated successfully");
      } else {
        await createHospital({ ...data }).unwrap();
        toast.success("Hospital created successfully");
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving hospital:", error);
      toast.error("Failed to save hospital");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="hospital-form-modal"
      aria-describedby="hospital-form-modal"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiBackdrop-root": {
          background: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: 1000,
          maxHeight: "90vh",
          overflowY: "auto",
          p: 3,
          position: "relative",
        }}
        elevation={3}
      >
        <Box
          sx={{ width: "100%" }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid container spacing={2}>
            {/** Left: Hospital details */}
            <Grid size={12}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Name"
                            error={!!errors.name}
                            helperText={errors.name?.message}
                          />
                        )}
                      />
                      <Box sx={{ mt: 1 }}>
                        <Grid container spacing={1}>
                          <Grid size={12}>
                            <Controller
                              name="address.street"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  label="Street Address"
                                  error={!!errors.address?.street}
                                  helperText={errors.address?.street?.message}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={12}>
                            <Controller
                              name="address.city"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  label="City"
                                  error={!!errors.address?.city}
                                  helperText={errors.address?.city?.message}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={12}>
                            <Controller
                              name="address.state"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  label="State/Province"
                                  error={!!errors.address?.state}
                                  helperText={errors.address?.state?.message}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={12}>
                            <Controller
                              name="address.postCode"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  label="Post Code"
                                  error={!!errors.address?.postCode}
                                  helperText={errors.address?.postCode?.message}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={12}>
                            <Controller
                              name="address.country"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  label="Country"
                                  error={!!errors.address?.country}
                                  helperText={errors.address?.country?.message}
                                />
                              )}
                            />
                          </Grid>
                          <Grid size={12}>
                            <Controller
                              name="address.state"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  label="State/Province"
                                  error={!!errors.address?.state}
                                  helperText={errors.address?.state?.message}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={1} alignItems={"center"}>
                      <Tooltip title="Contact phone">
                        <IconButton size="small" aria-label="phone" disabled>
                          <Phone fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder="Enter phone number"
                            error={!!errors.phone}
                            helperText={errors.phone?.message}
                          />
                        )}
                      />
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems={"center"}>
                      <Tooltip title="Contact email">
                        <IconButton size="small" aria-label="email" disabled>
                          <Email fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder="Enter email"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                          />
                        )}
                      />
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={1} alignItems="center">
                    <Grid size={12}>
                      <Controller
                        name="totalBeds"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Total Beds"
                            type="number"
                            variant="outlined"
                            error={!!errors.totalBeds}
                            helperText={errors.totalBeds?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Controller
                        name="icuBeds"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="ICU Beds"
                            type="number"
                            variant="outlined"
                            error={!!errors.icuBeds}
                            helperText={errors.icuBeds?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Controller
                        name="ventilatorBeds"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Ventilator Beds"
                            type="number"
                            variant="outlined"
                            error={!!errors.ventilatorBeds}
                            helperText={errors.ventilatorBeds?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/**Right: Bed details */}
            <Grid size={12}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid size={12}>
                      <Card variant="outlined" sx={{ p: 1 }}>
                        <Typography variant="caption">Free Beds</Typography>
                        <Controller
                          name="availableBeds"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              size="small"
                              type="number"
                              variant="outlined"
                              error={!!errors.availableBeds}
                              helperText={errors.availableBeds?.message}
                            />
                          )}
                        />
                      </Card>
                    </Grid>
                    <Grid size={12}>
                      <Card variant="outlined" sx={{ p: 1 }}>
                        <Typography variant="caption">Free ICU Beds</Typography>
                        <Controller
                          name="icuAvailableBeds"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              size="small"
                              type="number"
                              variant="outlined"
                              error={!!errors.icuAvailableBeds}
                              helperText={errors.icuAvailableBeds?.message}
                            />
                          )}
                        />
                      </Card>
                    </Grid>
                    <Grid size={12}>
                      <Card variant="outlined" sx={{ p: 1 }}>
                        <Typography variant="caption">
                          Free Ventilator Beds
                        </Typography>
                        <Controller
                          name="ventilatorAvailableBeds"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              size="small"
                              type="number"
                              variant="outlined"
                              error={!!errors.ventilatorAvailableBeds}
                              helperText={
                                errors.ventilatorAvailableBeds?.message
                              }
                            />
                          )}
                        />
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/**Botton: Opening hours + Map (full width) */}
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 1 }}
                      >
                        <AccessTime />
                        <Typography variant="h6">Opening hours</Typography>
                      </Stack>

                      <Table size="small" aria-label="opening hours">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Day
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Opening Time
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Closing Time
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.keys(hospital?.operatingHours)?.map?.(
                            (key: string) => (
                              <TableRow key={key}>
                                <TableCell
                                  component="th"
                                  sx={{ borderBottom: "none", py: 0.5 }}
                                >
                                  <Typography variant="caption">
                                    {key.toUpperCase()}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Controller
                                    name={`operatingHours.${key}.start`}
                                    control={control}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        size="small"
                                        type="time"
                                        variant="outlined"
                                        InputLabelProps={{
                                          shrink: true,
                                        }}
                                        inputProps={{
                                          step: 300, // 5 min
                                        }}
                                        sx={{
                                          "& .MuiInputBase-input": {
                                            py: 0.5,
                                            fontSize: "0.875rem",
                                          },
                                        }}
                                      />
                                    )}
                                  />
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Controller
                                    name={`operatingHours.${key}.end`}
                                    control={control}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        size="small"
                                        type="time"
                                        variant="outlined"
                                        InputLabelProps={{
                                          shrink: true,
                                        }}
                                        inputProps={{
                                          step: 300, // 5 min
                                        }}
                                        sx={{
                                          "& .MuiInputBase-input": {
                                            py: 0.5,
                                            fontSize: "0.875rem",
                                          },
                                        }}
                                      />
                                    )}
                                  />
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={6}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent sx={{ p: 0, height: { xs: 300, md: 200 } }}>
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          position: "relative",
                        }}
                      >
                        <MapPicker
                          onLocationSelect={() => {}}
                          initialLocation={{
                            lat: hospital.location?.coordinates?.[1] || 0,
                            lng: hospital.location?.coordinates?.[0] || 0,
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Add this at the bottom of your form, before the final closing Box */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {isEdit ? "Update Hospital" : "Create Hospital"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default HospitalFormModal;
